// ─── Broadcast Types ────────────────────────────────────────────────────────

export interface Broadcast {
  id: string
  stream_key: string
  state: 'ready' | 'live' | 'complete' | 'deleted'
  metadata: Record<string, string>
  createdAt: string
  /**
   * When this ingest key was last rotated out from under its owner, which the
   * API does when the free trial is enforced. Non-null means the key saved in
   * the user's encoder is stale and has to be re-copied.
   */
  keyRotatedAt?: string | null
}

export interface ActiveLiveStream {
  id: string
  url: string
  broadcastId: string
  createdAt: string
}

/**
 * Where a broadcaster sits against their free allowance. Computed by the API so
 * the thresholds live in one place — never re-derive these from percentages here.
 *
 * `grace` is the one that surprises: the user is over the limit but a session is
 * still running, and that session is deliberately left alone. The page stays
 * live; only starting something new is blocked.
 */
export type TrialPhase = 'exempt' | 'active' | 'warning' | 'grace' | 'expired'

/** The authenticated user's free-trial streaming usage, from GET /broadcasts/me/trial-status. */
export interface TrialStatus {
  /** Cumulative seconds streamed across all completed sessions. */
  pastSeconds: number
  /** Start time of the current live session, if any. */
  activeSince: string | null
  /** Free trial limit, in seconds. */
  limitSeconds: number
  /** Seconds at which the soft nudge fires, below `limitSeconds`. */
  warnAtSeconds: number
  /**
   * Seconds at which a running stream is cut off for real. The gap between this
   * and `limitSeconds` is the grace window.
   */
  hardLimitSeconds: number
  /** True when exempt from the trial limit (internal admin or active subscription). */
  isExempt: boolean
  /** Where the user sits against the allowance. Drive UI off this. */
  phase: TrialPhase
  /**
   * True when streaming is over for good and the ingest keys have been rotated.
   * Equivalent to `phase === 'expired'`. Note this is *not* simply "past the
   * limit" — a stream still running in the grace window is past the limit but
   * not expired.
   */
  isExpired: boolean
}

/** A recording (MP4 transcode) — nested inside EndedLiveStream */
export interface Recording {
  id: string
  userId: string
  liveStreamId: string
  metadata: Record<string, string>
  url: string | null
  progress: number
  signature: string | null
  createdAt: string
  updatedAt: string
  thumbnail: string | null
}

/** On-chain recording data — separate 1:1 table joined via chainData relation */
export interface LiveStreamChainData {
  /** bytes32 hex — on-chain streamId from LoreStreams contract */
  chainStreamId: string | null
  /** TX hash for recordStreamStart (TX 1) */
  startTxHash: string | null
  /** TX hash for recordStreamStop (TX 2) */
  stopTxHash: string | null
  /** TX hash for recordStreamMP4 (TX 3) */
  mp4TxHash: string | null
  /** Contract address that recorded this stream on-chain */
  chainContractAddress: string | null
  /** Block explorer base URL for this contract/network */
  chainExplorerUrl: string | null
  /** True once all applicable chain transactions have been recorded */
  chainRecorded: boolean
}

/** On-chain clip data — separate 1:1 table joined via clip.chainData relation */
export interface ClipChainData {
  /** TX hash for recordClip */
  clipTxHash: string | null
  /** Contract address that recorded this clip on-chain */
  chainContractAddress: string | null
  /** Block explorer base URL for this contract/network */
  chainExplorerUrl: string | null
  /** True once the clip TX has been confirmed on-chain */
  chainRecorded: boolean
}

// ─── Stream Categories ───────────────────────────────────────────────────────

export type StreamCategory =
  | 'news'
  | 'media'
  | 'personal'
  | 'marketing'
  | 'public-interest'
  | 'political'
  | 'sports'
  | 'music'
  | 'other'

export const STREAM_CATEGORIES: { value: StreamCategory; label: string }[] = [
  { value: 'news', label: 'News' },
  { value: 'media', label: 'Media' },
  { value: 'personal', label: 'Personal' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'public-interest', label: 'Public Interest' },
  { value: 'political', label: 'Political' },
  { value: 'sports', label: 'Sports' },
  { value: 'music', label: 'Music' },
  { value: 'other', label: 'Other' },
]

/** A LiveStream that has ended — returned by GET /broadcasts/streams */
export interface EndedLiveStream {
  id: string
  url: string
  broadcastId: string
  metadata: Record<string, string>
  initialSignature: string | null
  finalSignature: string | null
  createdAt: string
  endedAt: string
  updatedAt: string
  duration: number | null
  recording: Recording | null
  /** On-chain data — joined from separate LiveStreamChainData table (null if no chain data) */
  chainData: LiveStreamChainData | null
  /** Whether the user has deleted HLS playback files from storage */
  hlsDeleted?: boolean
  thumbnail?: string
  /** Whether the user has opted to show this stream on the public ledger */
  publicLedger?: boolean
  /** User-editable stream title (auto-populated from metadata.title) */
  title?: string | null
  /** Link to the original stream on another platform */
  originalUrl?: string | null
  /** User-assigned content category */
  category?: StreamCategory | null
  /** Field keys hidden from the public ledger card. Null/empty = all visible. */
  ledgerVisibility?: string[] | null
  /** User info — only included in public ledger responses */
  user?: {
    username: string | null
    picture: string | null
    avatarUrl: string | null
  } | null
  /** Clips for this stream — only included on the ledger endpoint, each may have chainData */
  clips?: Clip[]
}

// ─── Stream Status ────────────────────────────────────────────────────────────

export interface StreamStatus {
  /** ISO 8601 timestamp — when the stream started (from ActiveLiveStream.createdAt) */
  startTime: string
  /** ISO 8601 timestamp — when the stream ends / ended (null while live) */
  stopTime: string | null
}

// ─── Verification Types ──────────────────────────────────────────────────────

export type VerificationPhase = 'idle' | 'waiting' | 'verified' | 'paused'

export interface SegmentVerification {
  segmentName: string
  verified: boolean
  timestamp: Date
}

export interface VerificationState {
  phase: VerificationPhase
  publicKeyUrl: string | null
  publicKeyLoaded: boolean
  segments: SegmentVerification[]
  totalVerified: number
  totalFailed: number
}

// ─── Clip Types ──────────────────────────────────────────────────────────────

export type ClipState = 'queued' | 'processing' | 'complete'

export interface Clip {
  id: string
  liveStreamId: string
  state: ClipState
  startSecond: number
  duration: number
  url: string | null
  thumbnail: string | null
  progress: number
  signature: string | null
  createdAt: string
  updatedAt: string
  /** Optional user-supplied title (from the clip-modal input). Null = fall back to "Clip N" in the UI. */
  title?: string | null
  /** On-chain clip data — joined from separate ClipChainData table (null if no chain data) */
  chainData?: ClipChainData | null
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Format a duration in seconds as HH:MM:SS. */
export function formatTrialDuration(totalSeconds: number): string {
  const clamped = Math.max(0, Math.floor(totalSeconds))
  const hours = Math.floor(clamped / 3600)
  const minutes = Math.floor((clamped % 3600) / 60)
  const seconds = clamped % 60
  return [hours, minutes, seconds].map((n) => String(n).padStart(2, '0')).join(':')
}

/** Format an ISO timestamp to a human-readable string in the user's local timezone. */
export function formatStreamTime(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleString('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
    hour12: false,
  })
}
